import React, { useMemo } from 'react';
import { Checkbox } from '@components-library/Forms/Checkbox/Checkbox';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Column } from '@tanstack/react-table';
import cn from 'classnames';
import { IGridVisibilityDropdownProps } from './GridVisibilityDropdown';
import { SortableItem } from './SortableItem';
import styles from './gridVisibilityDropdown.module.scss';

export const DropdownInner = ({
  table,
  columnVisibility,
  columnOrder,
  setColumnOrder,
}: Pick<IGridVisibilityDropdownProps, 'columnVisibility' | 'table' | 'columnOrder' | 'setColumnOrder'>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const defaultColumnsOrder = table.getAllLeafColumns().map((column) => column.id);
  const sanitizedColumnOrder = columnOrder.filter((value) => defaultColumnsOrder.includes(value));
  const lazyColumnOrderList = columnOrder.length ? sanitizedColumnOrder : defaultColumnsOrder;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const items = lazyColumnOrderList;
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over?.id as string);

      const result = arrayMove(items, oldIndex, newIndex);

      setColumnOrder(result);
    }
  };

  const columnIdToColumnMap = useMemo(
    () =>
      table.getAllLeafColumns().reduce<{ [id: string]: Column<unknown, unknown> }>((acc, column) => {
        acc[column.id] = column;
        return acc;
      }, {}),
    [table],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={lazyColumnOrderList} strategy={verticalListSortingStrategy}>
        {lazyColumnOrderList.map((id) => {
          const column = columnIdToColumnMap[id];

          if (!column) {
            console.error('Column cannot be displayed. It was deleted or does not exist anymore.');
            return <React.Fragment key={id}></React.Fragment>;
          }

          const { header } = column.columnDef;
          const isChecked = columnVisibility[column.id] ?? true;

          const isDisabled = id === 'id' || id === 'actions';

          return (
            <SortableItem key={id} id={id} isDisabled={isDisabled}>
              <Checkbox
                rootClassName={styles.checkboxRoot}
                className={cn(styles.checkbox, styles.textContainer)}
                checked={isChecked}
                onChange={() => column.toggleVisibility(!isChecked)}
                disabled={isDisabled}>
                {typeof header === 'function' ? header({} as never) : header}
              </Checkbox>
            </SortableItem>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};
